/**
 * @fileoverview PHARMAKON — Trésorerie : constantes client du temps réel (TWP-11).
 * @module config/tresorerie.config
 *
 * 02_ARCHITECTURE §11, ADR-11 — TA-08 (contrat d'invalidation), TD-60 (sujet sur le flux unique +
 * plancher de version). Les VALEURS métier (types, motifs, comptes, droits) restent servies par
 * `GET /tresorerie-pharmacie/referentiel` ; ce fichier ne porte que le VOCABULAIRE des clés
 * d'invalidation (un contrat de transport) et les cadences par défaut du plancher, que la première
 * réponse de `GET /version` remplace (le serveur décide).
 *
 * MIROIR : `FAMILLES_CLES_TRESORERIE`, `CLE_TRESORERIE_SEPARATEUR`, `CLE_TRESORERIE_JOKER` et
 * `sujet` sont égaux à ceux de `apps/server/src/config/tresorerie-pharmacie.config.js` — vérifié par
 * `apps/server/src/services/__tests__/tresorerie-temps-reel.unit.test.js`.
 */

/** Les familles de clés publiées par le serveur après commit (pointeurs, jamais un montant). */
export const FAMILLES_CLES_TRESORERIE = Object.freeze({
  OPERATIONS: "operations",
  KPI: "kpi",
  REGLEMENTS: "reglements",
  ECHEANCES: "echeances",
  RECURRENTS: "recurrents",
  SIGNAUX: "signaux",
  REFERENTIEL: "referentiel",
  MOTIFS: "motifs",
  PARAMETRES: "parametres",
  COMPTE: "compte",
  ECRITURES: "ecritures",
  FACTURE: "facture",
  TIERS: "tiers",
  CHARGE: "charge",
  SESSION: "session",
});

export const CLE_TRESORERIE_SEPARATEUR = ":";
export const CLE_TRESORERIE_JOKER = "*";

/** `<famille>:<id>` — la clé d'UN objet d'une famille à identifiant. */
export function cleTresorerie(famille, id) {
  return `${famille}${CLE_TRESORERIE_SEPARATEUR}${id}`;
}

/**
 * Le temps réel côté client :
 *   · `sujet` : le sujet des trames de trésorerie sur le flux de l'officine ;
 *   · `plancher_visible_ms` / `plancher_cache_ms` : cadence de `GET /version` avant la première
 *     réponse du serveur (ensuite : `plancher_ms` de la réponse) ;
 *   · `conservation_ms` : une entrée que plus aucun écran ne lit est oubliée après ce délai (un
 *     retour sur l'écran dans l'intervalle réaffiche aussitôt la dernière lecture, puis la relit) ;
 *   · `ecritures_locales_max` : les écritures de CET onglet mémorisées pour ne pas relire deux fois
 *     ce que la réponse a déjà rafraîchi quand la trame correspondante arrive.
 */
export const TEMPS_REEL_TRESORERIE_CLIENT = Object.freeze({
  sujet: "treso",
  plancher_visible_ms: 60_000,
  plancher_cache_ms: 300_000,
  conservation_ms: 300_000,
  ecritures_locales_max: 50,
});

/**
 * TWP-18 — « Sessions de caisse » (UX 02 §9.5): server-paged, 50 rows per page on the leaf (TD-65),
 * 20 in a comptoir's drawer tab (≤ the server's `SESSIONS_CAISSE_PAGE_MAX`).
 */
export const SESSIONS_PAGE_TAILLE = 50;
export const SESSIONS_PAGE_TAILLE_COMPACTE = 20;

// ─── TWP-26 — « Règlements » : le vocabulaire des requêtes (un contrat de transport) ──────────────
//
// MIROIR des valeurs d'URL que le serveur accepte (`apps/server/src/config/tresorerie-pharmacie.config.js`
// `SOLDES_FILTRE_SOLDE`, `SOLDES_FILTRE_ECHEANCE`, `SOLDES_TRI`, leurs défauts, `RECHERCHE_FOURNISSEUR_MIN`,
// `STATUT_OPERATION_TRESORERIE` du filtre « Statut » du journal) — vérifié par
// `apps/server/src/services/__tests__/tresorerie-reglements-ui-twp26.unit.test.js`. Les libellés restent
// ceux de l'écran (config/tresorerie-reglements.messages.js) ; les montants, les tuiles et les droits sont
// servis.

/** UX 03 §3.4 « Afficher » (`solde`). */
export const SOLDES_FILTRE_SOLDE = Object.freeze({ A_PAYER: "a_payer", CREDIT: "credit", NON_NUL: "non_nul", TOUS: "tous" });
export const SOLDES_FILTRE_SOLDE_DEFAUT = SOLDES_FILTRE_SOLDE.A_PAYER;
/** UX 03 §3.4 « Échéance » (`echeance`; « à N jours » = le paramètre TD-85 servi par la réponse). */
export const SOLDES_FILTRE_ECHEANCE = Object.freeze({ TOUTES: "toutes", EN_RETARD: "en_retard", HORIZON: "horizon", NON_RENSEIGNEE: "non_renseignee" });
/** UX 03 §3.4 — les tris que le serveur sait faire (`tri`). */
export const SOLDES_TRI = Object.freeze({ SOLDE: "solde", ECHEANCE: "echeance", FOURNISSEUR: "fournisseur", REGLEMENTS: "reglements" });
export const SOLDES_TRI_DEFAUT = SOLDES_TRI.SOLDE;
/** Une recherche de fournisseur commence à deux caractères (01F §3.4). */
export const RECHERCHE_FOURNISSEUR_MIN = 2;
/** UX 03 §5.3 — le filtre « Statut » du journal des règlements (`statut`). */
export const JOURNAL_REGLEMENTS_FILTRE_STATUT = Object.freeze({ BROUILLON: "BROUILLON", CONFIRME: "CONFIRME", ANNULE: "ANNULE" });
/** TD-65 — une page de règlements : 50 lignes (≤ `REGLEMENTS_PAGE_MAX` du serveur). */
export const REGLEMENTS_PAGE_TAILLE = 50;

/**
 * TWP-27 — « Règlements › Charges » (UX 03 §13): the page size of the keyset list (`GET /charges`,
 * ≤ `CHARGES_PAGE_MAX` 200 server-side) and the search's minimum (the server's `CHARGES_RECHERCHE_MIN`).
 */
export const CHARGES_PAGE_TAILLE = 50;
export const RECHERCHE_CHARGE_MIN = 2;
/** UX 03 §4.3 — sous cette hauteur de fenêtre, les champs du paiement se replient après le choix de ventilation. */
export const REGLEMENT_REPLI_HAUTEUR_PX = 900;

// ─── TWP-29 — « Mouvements » (J1 / J2) : le vocabulaire des requêtes du journal (un contrat de transport) ─
//
// MIROIR des noms et des bornes que `GET /operations` et `GET /operations/export` acceptent
// (`apps/server/src/config/tresorerie-journal.config.js` : `VUE_JOURNAL`, `RECHERCHE_JOURNAL_MIN` / `_MAX`,
// `REFERENCE_FILTRE_MAX`, `JOURNAL_IDS_MAX`, `UTILISATEUR_SYSTEME_JOURNAL`, `PAR_UTILISATEUR_JOURNAL`,
// `FLUX_JOURNAL`, `EXPORT_JOURNAL` ; `tresorerie-pharmacie.config.js` : `JOURNAL_PAGE_DEFAUT`,
// `JOURNAL_PAGE_MAX`) — vérifié par `apps/server/src/services/__tests__/tresorerie-journal-ui-twp29.unit.test.js`.
// Les libellés (motifs, modes, origines, natures, statuts, types) restent servis par le référentiel.

/** UX 03 §9.4 — `?vue=`, dans l'ordre des onglets. */
export const VUES_JOURNAL = Object.freeze({
  TOUS: "tous",
  ENCAISSEMENTS: "encaissements",
  DECAISSEMENTS: "decaissements",
  TRANSFERTS: "transferts",
  CAISSE: "caisse",
  A_CONFIRMER: "a-confirmer",
  ANNULEES: "annulees",
});
/** F2 (TA-42) : 2 caractères au moins, 100 au plus ; « Référence contient » 100 au plus ; 50 ids par filtre multiple. */
export const JOURNAL_RECHERCHE_MIN = 2;
export const JOURNAL_RECHERCHE_MAX = 100;
export const JOURNAL_REFERENCE_MAX = 100;
export const JOURNAL_IDS_MAX = 50;
/** « Saisi par » : un utilisateur, ou « Système » ; `par` = la saisie (défaut) ou la confirmation. */
export const JOURNAL_UTILISATEUR_SYSTEME = "systeme";
export const JOURNAL_PAR_UTILISATEUR = Object.freeze({ SAISIE: "saisie", CONFIRMATION: "confirmation" });
/** A-DICT-01 — `flux=entrant|sortant` (un lien d'analyse). */
export const JOURNAL_FLUX = Object.freeze({ ENTRANT: "entrant", SORTANT: "sortant" });
/** TD-65 — une page du journal : 50 lignes ; le serveur en sert 200 au plus par requête. */
export const JOURNAL_PAGE_TAILLE = 50;
export const JOURNAL_PAGE_MAX = 200;
/** TD-268 — l'export du filtre : CSV ou Excel, synchrone jusqu'à `max_lignes`, refusé au-delà (TD-192). */
export const JOURNAL_EXPORT = Object.freeze({
  formats: Object.freeze(["csv", "xlsx"]),
  format_defaut: "csv",
  nom_fichier: "journal-des-mouvements",
  max_lignes: 10_000,
});

// CONFIG de l'écran (classe « build-time », L-2) — pas un miroir :
/** UX 03 §9.5 — la ligne « n opérations correspondent » du tiroir des filtres : `limite=0`, 400 ms après la dernière frappe. */
export const JOURNAL_COMPTE_FILTRES_DELAI_MS = 400;
/** La recherche F2 : 250 ms après la dernière frappe (01F §3.4). */
export const JOURNAL_RECHERCHE_DELAI_MS = 250;
/**
 * TD-357 « défaut appliqué — à valider par le propriétaire » — l'impression A4 (Ctrl+P) du filtre : au plus
 * 1 000 opérations (≈ 40 pages A4 paysage) ; au-delà, refusée avec le nombre et la borne (jamais une
 * impression tronquée sans le dire) — l'export (Alt+X) reste le chemin des gros volumes.
 */
export const JOURNAL_IMPRESSION_MAX_LIGNES = 1_000;
/** UX 03 §9.6 (TA-40) — les filtres de la session de travail, relus quand « Mouvements » s'ouvre sans paramètre. */
export const JOURNAL_FILTRES_SESSION_CLE = "treso.journal.filtres";
